// ---- Effects ----
export type Effect =
  | { kind: 'none' }
  | { kind: 'blur'; px: number }
  | { kind: 'grayscale'; amt: number }
  | { kind: 'glow'; color: string; radius: number; passes: number }
  | { kind: 'outline'; color: string; width: number }
  | { kind: 'crt'; intensity: number }
  | { kind: 'chromatic'; offset: number }
  | { kind: 'frostedGlass'; blur: number; tint: string };

export type EffectKind = Effect['kind'];

// ---- Theme tokens ----
export interface Typography {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string | number;
  lineHeight?: string | number;
  letterSpacing?: string;
  fontFeature?: string;
  fontVariation?: string;
}

/** A complete set of design tokens for one mode (light or dark). */
export interface ThemeModeTokens {
  colors: Record<string, string>;
  typography: Record<string, Typography>;
  rounded: Record<string, string>;
  spacing: Record<string, string | number>;
  components?: Record<string, Record<string, string>>;
  shadow?: Record<string, string>;
  motion?: Record<string, string>;
}

export interface ThemeToken {
  id: string;
  name: string;
  description?: string;
  // Light set (default):
  colors: Record<string, string>;
  typography: Record<string, Typography>;
  rounded: Record<string, string>;
  spacing: Record<string, string | number>;
  components?: Record<string, Record<string, string>>;
  shadow?: Record<string, string>;
  motion?: Record<string, string>;
  /** Optional full dark set; absent → dark mode renders the light tokens.
   *  Stored fields are merged on update so this may be partial after a patch. */
  dark?: Partial<ThemeModeTokens>;
}

/** Project a theme's top-level (light) fields into a ThemeModeTokens. */
export function lightTokens(t: ThemeToken): ThemeModeTokens {
  return {
    colors: t.colors,
    typography: t.typography,
    rounded: t.rounded,
    spacing: t.spacing,
    components: t.components,
    shadow: t.shadow,
    motion: t.motion,
  };
}

/** The token set for the effective mode: dark overlaid PER-KEY on light (so a
 *  partial dark set keeps the light siblings, matching the CSS cascade), else light. */
export function effectiveModeTokens(t: ThemeToken, isDark: boolean): ThemeModeTokens {
  const light = lightTokens(t);
  if (!isDark || !t.dark) return light;
  const d = t.dark;
  const mergeGroup = (l: Record<string, unknown> | undefined, r: Record<string, unknown> | undefined) =>
    l || r ? { ...(l ?? {}), ...(r ?? {}) } : undefined;
  return {
    colors: { ...light.colors, ...(d.colors ?? {}) },
    typography: { ...light.typography, ...(d.typography ?? {}) },
    rounded: { ...light.rounded, ...(d.rounded ?? {}) },
    spacing: { ...light.spacing, ...(d.spacing ?? {}) },
    components: mergeGroup(light.components, d.components),
    shadow: mergeGroup(light.shadow, d.shadow),
    motion: mergeGroup(light.motion, d.motion),
  } as ThemeModeTokens;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

// ---- Slot schema ----
export interface SlotSchema {
  name: string;
  defaultText: string;
  cfNodeId: string;
}

// ---- Motion timeline ----
export type MotionProp =
  | 'x' | 'y' | 'rotate' | 'scale' | 'scaleX' | 'scaleY'
  | 'opacity' | 'color' | 'backgroundColor' | 'blur';

export type EaseSpec =
  | 'linear' | 'easeIn' | 'easeOut' | 'easeInOut'
  | { bezier: [number, number, number, number] }
  | { spring: { stiffness?: number; damping?: number; mass?: number } }
  | 'step';

export interface Keyframe {
  t: number;
  value: number | string;
  ease: EaseSpec;
}

export interface MotionChannel {
  prop: MotionProp;
  keyframes: Keyframe[];
}

export interface MotionLayer {
  id: string;
  blend: 'replace' | 'add';
  channels: MotionChannel[];
}

export interface MotionTrack {
  id: string;
  target: string;
  label?: string;
  layers: MotionLayer[];
}

export interface Trigger {
  on: 'hover' | 'click' | 'focus' | 'leave';
  do: 'play' | 'reverse' | 'toggle' | 'goto';
  target?: string;
  segment?: [number, number];
}

export interface PlaybackConfig {
  autoplay?: { mode: 'once' | 'loop' | 'pingpong'; delay?: number; count?: number };
  triggers?: Trigger[];
  scroll?: { source: 'frameInView' | string; range: [number, number]; maps: [number, number] };
}

export interface MotionTimeline {
  duration: number;
  fps?: number;
  tracks: MotionTrack[];
  playback: PlaybackConfig;
}

// ---- Frame variants ----
export interface FrameBase {
  id: string;
  x: number;
  y: number;
  w: number;
  h?: number;
  z: number;
  title: string;
  effect: Effect;
  /** Tool frames only: id of the design frame this tool acts on.
   *  Undefined = global scope (acts on whole canvas). */
  anchorId?: string;
  /** Optional reference to a theme token. */
  themeId?: string;
}

export interface FreeFrame extends FrameBase {
  kind: 'free';
  html: string;
  /** Optional TypeScript code module exporting a behavior function. */
  script?: string;
  motion?: MotionTimeline;
}

export interface MainFrame extends FrameBase {
  kind: 'main';
  html: string;
  slots: SlotSchema[];
  locked: boolean;
  /** Optional TypeScript code module exporting a behavior function. */
  script?: string;
  motion?: MotionTimeline;
}

export interface InstanceFrame extends FrameBase {
  kind: 'instance';
  mainId: string;
  slotValues: { [slotName: string]: string };
}

// ---- TSX frame prop schema ----
export type PropValue = string | number | boolean;

export interface PropSpec {
  name: string;
  type: 'text' | 'number' | 'boolean' | 'enum';
  default: PropValue;
  options?: string[]; // enum only
}

// ---- TSX frame variants ----
export interface TsxFreeFrame extends FrameBase {
  kind: 'tsxFree';
  tsx: string;
  motion?: MotionTimeline;
}

export interface TsxMainFrame extends FrameBase {
  kind: 'tsxMain';
  tsx: string;
  propsSchema: PropSpec[];
  locked: boolean;
  motion?: MotionTimeline;
}

export interface TsxInstanceFrame extends FrameBase {
  kind: 'tsxInstance';
  mainId: string;
  propValues: Record<string, PropValue>;
}

// ---- Tool frame variants (canvas-native UI) ----
export interface ChatMessage {
  /** Stable id for in-place streaming updates (chat.patchMessage). Optional:
   *  user/step/summary messages don't need it. */
  id?: string;
  role: 'user' | 'assistant' | 'system';
  text: string;
  ts: number;
  /** Identity of the human author (email/sub), for multiplayer attribution.
   *  Chat is shared CRDT state, so a `role:'user'` message needs an author to
   *  avoid labelling everyone's messages "You". Optional + backward-compatible:
   *  legacy/unattributed messages render as "Someone". Assistant = Viv (by role).
   */
  authorSub?: string;
}

export interface ChatFrame extends FrameBase {
  kind: 'chat';
  messages: ChatMessage[];
  draft: string;
  /** false = ephemeral (auto-dismiss on outside click). ⌘K bubbles start
   *  pinned: false. Pin button flips to true. */
  pinned: boolean;
  /** Owned by the docked Editor Panel: persists in the doc (per-frame history)
   *  but is never rendered on the canvas nor listed in the agent snapshot. */
  panelOwned?: boolean;
}

export interface CodeFrame extends FrameBase {
  kind: 'code';
  // Stateless projection of anchor's html (Free/Main) or resolved html
  // (Instance, read-only). No own content fields.
  /** Owned by the docked Editor Panel — hidden from canvas + agent snapshot. */
  panelOwned?: boolean;
}

export interface FxFrame extends FrameBase {
  kind: 'fx';
  // Stateless projection of anchor's effect. No own content fields.
}

export interface StyleguideFrame extends FrameBase {
  kind: 'styleguide';
  // Visualizes a theme: which theme via FrameBase.themeId; falls back to the
  // canvas activeThemeId when themeId is undefined. anchorId unused.
}

// ---- Annotation (note) tool frame ----
export type NoteTarget =
  | { kind: 'frame'; frameId: string }
  | { kind: 'pin'; x: number; y: number }
  | { kind: 'element'; frameId: string; nodeId: string };

/** A selected sub-element ("layer") inside an html design frame. */
export interface LayerRef {
  frameId: string;
  cfNodeId: string;
}

/** Where a moved node lands relative to a reference node. */
export type MovePosition = 'before' | 'after' | 'inside';

export interface NoteFrame extends FrameBase {
  kind: 'note';
  /** Live HTML body, rendered like a design frame. */
  html: string;
  /** What the leader/pin points at. Undefined = floating note (no leader). */
  target?: NoteTarget;
  /** Tour membership; ordering within a tour is by `step`. */
  tourId?: string;
  /** 1-based position within its tour. */
  step?: number;
  /** Optional accent override; defaults to --color-tool-note. */
  color?: string;
}

export interface Tour {
  id: string;
  title: string;
}

// ---- Comments (threaded feedback on a layer/frame; read by the agent) ----
export interface CommentMsg {
  id: string;
  author: 'user' | 'agent';
  text: string;
  ts: number;
}
export interface CommentThread {
  id: string;
  /** Anchor — reuse the note anchor (element layer / frame / canvas pin). */
  target: NoteTarget;
  resolved: boolean;
  messages: CommentMsg[];
}

export type DesignFrame = FreeFrame | MainFrame | InstanceFrame | TsxFreeFrame | TsxMainFrame | TsxInstanceFrame;
export type ToolFrame = ChatFrame | CodeFrame | FxFrame | NoteFrame | StyleguideFrame;
export type Frame = DesignFrame | ToolFrame;
export type FrameKind = Frame['kind'];

export function isToolFrame(f: Frame): f is ToolFrame {
  return f.kind === 'chat' || f.kind === 'code' || f.kind === 'fx' || f.kind === 'note' || f.kind === 'styleguide';
}
export function isDesignFrame(f: Frame): f is DesignFrame {
  return (
    f.kind === 'free' ||
    f.kind === 'main' ||
    f.kind === 'instance' ||
    f.kind === 'tsxFree' ||
    f.kind === 'tsxMain' ||
    f.kind === 'tsxInstance'
  );
}
/** A tool frame owned by the docked Editor Panel — hidden from canvas + agent snapshot. */
export function isPanelOwned(f: Frame): boolean {
  return (f.kind === 'chat' || f.kind === 'code') && !!f.panelOwned;
}

// ---- Viewport + Doc ----
export interface Viewport {
  pan: { x: number; y: number };
  zoom: number;
}

export interface CanvasDoc {
  version: 2;
  frames: Frame[];
  viewport: Viewport;
  /** Tour registry (title only); step order derived from NoteFrame.step. */
  tours?: Tour[];
  /** Theme token registry; themes are referenced by id in FreeFrame/MainFrame.themeId. */
  themes?: ThemeToken[];
  /** The canvas-wide default theme; themeless frames inherit it via the cascade. */
  activeThemeId?: string;
  /** Threaded feedback comments anchored to layers/frames; read by the agent. */
  comments?: CommentThread[];
  /** Id allocation scheme. Absent/undefined = legacy positional cf-N (pre-migration);
   *  'hash' = content-hash ids already minted (migrateCfIds has run). */
  cfIdScheme?: 'hash';
}

// ---- Commands ----
export interface BatchPredicate {
  slotName?: string;
  slotEquals?: string;
  inRect?: Rect;
}

export type Command =
  // v1 carryover (frame.create still creates FreeFrame)
  | { t: 'frame.create'; id: string; x: number; y: number; w: number; title: string; html: string }
  | { t: 'frame.updateHtml'; id: string; html: string }
  | { t: 'frame.setEffect'; id: string; effect: Effect }
  | { t: 'frame.move'; ids: string[]; dx: number; dy: number }
  // x/y optional: top/left/corner handles reposition the origin as they resize
  // (B4 — w/h-only could not move the origin). Omitted → origin unchanged.
  | { t: 'frame.resize'; id: string; x?: number; y?: number; w: number; h: number }
  | { t: 'frame.delete'; ids: string[] }
  | { t: 'select'; ids: string[] }
  | { t: 'viewport.focus'; bounds: Rect }
  | { t: 'viewport.center'; bounds: Rect }
  | { t: 'viewport.set'; pan: { x: number; y: number }; zoom: number }
  // Main / Instance lifecycle
  | { t: 'frame.promoteToMain'; id: string; locked?: boolean }
  | { t: 'frame.demoteFromMain'; id: string }
  | { t: 'main.setLocked'; id: string; locked: boolean }
  | {
      t: 'instance.create';
      id: string;
      mainId: string;
      x: number;
      y: number;
      slotValues: { [k: string]: string };
      w?: number;
    }
  | { t: 'instance.setSlot'; id: string; slotName: string; value: string }
  | { t: 'instance.setSlots'; id: string; slotValues: { [k: string]: string } }
  | { t: 'instance.detach'; id: string }
  // Sub-element extract
  | {
      t: 'main.extractSubtree';
      mainId: string;
      cfNodeId: string;
      newMainId: string;
      x: number;
      y: number;
      title: string;
    }
  // Cross-canvas batch
  | {
      t: 'canvas.batchSetSlot';
      mainId: string;
      slotName: string;
      value: string;
      predicate?: BatchPredicate;
    }
  | {
      t: 'canvas.replaceInstances';
      fromMainId: string;
      toMainId: string;
      slotMapping: { [from: string]: string };
    }
  // Tool-frame lifecycle
  | {
      t: 'toolFrame.create';
      id: string;
      toolKind: 'chat' | 'code' | 'fx' | 'styleguide';
      x: number;
      y: number;
      anchorId?: string;
      pinned?: boolean;          // chat only; default false
      panelOwned?: boolean;      // chat/code only; hides from canvas + snapshot
    }
  | { t: 'toolFrame.setAnchor'; id: string; anchorId: string | null }
  | { t: 'toolFrame.pin'; id: string }
  | { t: 'chat.appendMessage'; id: string; message: ChatMessage }
  | { t: 'chat.patchMessage'; id: string; messageId: string; text: string }
  | { t: 'chat.setDraft'; id: string; draft: string }
  | { t: 'chat.clear'; id: string }
  // Annotations (notes) + tours
  | {
      t: 'note.create';
      id: string;
      x: number;
      y: number;
      w: number;
      html: string;
      title: string;
      target?: NoteTarget;
      tourId?: string;
      step?: number;
      color?: string;
    }
  | { t: 'note.setBody'; id: string; html: string }
  | { t: 'note.setTarget'; id: string; target?: NoteTarget }
  | { t: 'note.setMeta'; id: string; title?: string; tourId?: string; step?: number; color?: string }
  | { t: 'tour.upsert'; id: string; title: string }
  | { t: 'tour.remove'; id: string }
  | { t: 'frame.setScript'; id: string; script: string }
  | { t: 'frame.setTheme'; id: string; themeId: string | undefined }
  | { t: 'theme.create'; theme: ThemeToken }
  | { t: 'theme.update'; id: string; patch: Partial<ThemeToken> }
  | { t: 'theme.delete'; id: string }
  | { t: 'theme.importDesignMd'; source: string }
  | { t: 'doc.setActiveTheme'; themeId: string | undefined }
  // TSX frames
  | { t: 'tsxFrame.create'; id: string; x: number; y: number; w: number; h?: number; title: string; tsx: string }
  | { t: 'tsx.setSource'; id: string; tsx: string }
  | { t: 'tsxFrame.promoteToMain'; id: string; locked?: boolean }
  | { t: 'tsxInstance.create'; id: string; mainId: string; x: number; y: number; propValues: Record<string, PropValue>; w?: number }
  | { t: 'tsxInstance.setProp'; id: string; name: string; value: PropValue }
  | { t: 'tsxInstance.resetProp'; id: string; name: string }
  // Motion timeline commands
  | { t: 'motion.init'; frameId: string }
  | { t: 'motion.addTrack'; frameId: string; trackId: string; layerId: string; target: string; label?: string }
  | { t: 'motion.removeTrack'; frameId: string; trackId: string }
  | { t: 'motion.addLayer'; frameId: string; trackId: string; layerId: string; blend: 'replace' | 'add' }
  | { t: 'motion.setLayerBlend'; frameId: string; trackId: string; layerId: string; blend: 'replace' | 'add' }
  | { t: 'motion.setKeyframe'; frameId: string; trackId: string; layerId: string; prop: MotionProp; kf: Keyframe }
  | { t: 'motion.moveKeyframe'; frameId: string; trackId: string; layerId: string; prop: MotionProp; fromT: number; toT: number }
  | { t: 'motion.removeKeyframe'; frameId: string; trackId: string; layerId: string; prop: MotionProp; atT: number }
  | { t: 'motion.setEase'; frameId: string; trackId: string; layerId: string; prop: MotionProp; kfT: number; ease: EaseSpec }
  | { t: 'motion.setDuration'; frameId: string; duration: number }
  | { t: 'motion.setPlayback'; frameId: string; playback: PlaybackConfig }
  // Layer content editing (P3b)
  | { t: 'frame.editNode'; frameId: string; cfNodeId: string; text: string }
  | { t: 'frame.setNodeMedia'; frameId: string; cfNodeId: string; ref: string }
  // Layer structure editing (delete / duplicate a sub-element subtree)
  | { t: 'frame.deleteNode'; frameId: string; cfNodeId: string }
  | { t: 'frame.duplicateNode'; frameId: string; cfNodeId: string }
  | { t: 'frame.moveNode'; frameId: string; cfNodeId: string; refCfNodeId: string; position: MovePosition }
  // Incremental node insertion (set innerHTML / insert fragment)
  | { t: 'frame.setNodeHtml'; frameId: string; cfNodeId: string; html: string }
  | { t: 'frame.insertNode'; frameId: string; refCfNodeId: string; html: string; position: MovePosition }
  // Lottie slot overrides (rewrites the element's data-lottie-slots attribute)
  | { t: 'lottie.setSlots'; frameId: string; cfNodeId: string; slots: Record<string, number | number[] | string> }
  // Comments (threaded feedback + agent loop)
  | { t: 'comment.create'; id: string; target: NoteTarget; message: CommentMsg }
  | { t: 'comment.reply'; threadId: string; message: CommentMsg }
  | { t: 'comment.resolve'; threadId: string; resolved: boolean }
  | { t: 'comment.delete'; threadId: string }
  | { t: 'doc.clear' }
  // History restore (payload-carrying; UI reads the snapshot from IndexedDB first)
  | { t: 'frame.restore'; frame: Frame }
  | { t: 'doc.restore'; doc: CanvasDoc };

// ---- Canvas state ----
export interface CanvasState {
  doc: CanvasDoc;
  selection: string[];
  /** Ephemeral sub-element selection (one layer). Not serialized, not undoable. */
  layerSelection?: LayerRef | null;
  /** Ephemeral: which comment thread's popover is open. Not serialized, not undoable. */
  openCommentThreadId?: string | null;
}

